import { call, put, takeEvery, takeLatest, select, all, delay } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import {
  ncrApi,
  SearchNCRsRequest,
  SearchNCRsResponse,
  CreateNCRRequest,
  AddDefectRequest,
  UpdateSerialDispositionRequest,
  NCR,
  NCRSerial,
  PartDefect,
  NCRReportData
} from '../../services/ncrApi';
import {
  // List actions
  fetchNCRsStart,
  fetchNCRsSuccess,
  fetchNCRsFailure,
  
  // Selected NCR actions
  fetchNCRByIdStart,
  fetchNCRByIdSuccess,
  fetchNCRByIdFailure,
  
  // Create NCR actions
  createNCRStart,
  createNCRSuccess,
  createNCRFailure,
  
  // Update NCR actions
  updateNCRStart,
  updateNCRSuccess,
  updateNCRFailure,
  
  // Delete NCR actions
  deleteNCRStart,
  deleteNCRSuccess,
  deleteNCRFailure,
  
  // Dropdown actions
  fetchDropdownsStart,
  fetchDropdownsSuccess,
  fetchDropdownsFailure,
  
  // Serial management actions
  fetchSerialsStart,
  fetchSerialsSuccess,
  fetchSerialsFailure,
  updateSerialStart,
  updateSerialSuccess,
  updateSerialFailure,
  deleteSerialStart,
  deleteSerialSuccess,
  deleteSerialFailure,
  
  // Defect management actions
  addDefectStart,
  addDefectSuccess,
  addDefectFailure,
  deleteDefectStart,
  deleteDefectSuccess,
  deleteDefectFailure,
  
  // Step-wise NCR filling actions
  saveGeneralDetailsStart,
  saveGeneralDetailsSuccess,
  saveGeneralDetailsFailure,
  saveSerialsDispositionStart,
  saveSerialsDispositionSuccess,
  saveSerialsDispositionFailure,
  saveClosingStart,
  saveClosingSuccess,
  saveClosingFailure,
  
  // Report actions
  generateReportStart,
  generateReportSuccess,
  generateReportFailure,
  
  // UI state actions
  setSelectedWorkOrderId,
  setSelectedOperationId,
  setSelectedNCTypeId,
  
  // Statistics actions
  updateStatistics,
} from '../reducers/ncrSlice';
import { RootState } from '../index';

// Helper function to extract data from API responses
const extractData = (response: any): any => {
  console.log('NCR Saga: Processing API response:', response);
  
  // Parse response body if it's a string
  let parsedResponse;
  if (response.body && typeof response.body === 'string') {
    try {
      parsedResponse = JSON.parse(response.body);
      console.log('NCR Saga: Parsed response body:', parsedResponse);
    } catch (error) {
      console.error('NCR Saga: Failed to parse response body:', error);
      parsedResponse = response;
    }
  } else {
    parsedResponse = response;
  }
  
  // Extract the data
  let data;
  if (parsedResponse && parsedResponse.data) {
    data = parsedResponse.data;
    console.log('NCR Saga: Using parsedResponse.data:', data);
  } else if (parsedResponse && parsedResponse.items) {
    data = parsedResponse;
    console.log('NCR Saga: Using parsedResponse with items:', data);
  } else if (Array.isArray(parsedResponse)) {
    // Convert array to expected format - this is what we're getting from the API
    data = { 
      items: parsedResponse, 
      total_count: parsedResponse.length, 
      page_num: 1, 
      page_size: parsedResponse.length 
    };
    console.log('NCR Saga: Converting array to response format:', data);
  } else {
    data = parsedResponse;
    console.log('NCR Saga: Using parsedResponse as-is:', data);
  }
  
  console.log('NCR Saga: Final extracted data:', data);
  return data;
};

// NCR List Saga
function* fetchNCRsApi(action: PayloadAction<SearchNCRsRequest | undefined>): Generator<any, void, any> {
  try {
    const state: RootState = yield select();
    const { pagination, searchTerm, statusFilter, workOrderFilter, dateFromFilter, dateToFilter } = state.ncr;
    
    const params: SearchNCRsRequest = action.payload || {
      page_num: pagination.currentPage,
      page_size: pagination.pageSize,
      sort: 'created_date desc',
      filter: {
        created_date: dateFromFilter && dateToFilter ? `${dateFromFilter} to ${dateToFilter}` : undefined,
        work_order_id: workOrderFilter || undefined,
        closure_status: statusFilter !== 'All' ? statusFilter : undefined,
        ncr_folio_num: searchTerm || undefined,
      },
    };
    
    console.log('NCR Saga: Fetching NCRs with params:', params);
    const response: any = yield call(ncrApi.getAll, params);
    console.log('NCR Saga: Raw API response:', response);
    
    const extractedResponse = extractData(response);
    console.log('NCR Saga: Extracted response:', extractedResponse);
    console.log('NCR Saga: Response structure check:', {
      hasItems: !!extractedResponse.items,
      itemsLength: extractedResponse.items?.length,
      totalCount: extractedResponse.total_count,
      pageNum: extractedResponse.page_num,
      pageSize: extractedResponse.page_size
    });
    
    yield put(fetchNCRsSuccess(extractedResponse));
    
    // Update statistics
    if (extractedResponse.items && extractedResponse.items.length > 0) {
      const rawStatistics = ncrApi.calculateNCRStatistics(extractedResponse.items);
      const statistics = {
        totalOpen: rawStatistics.total_open,
        totalClosed: rawStatistics.total_closed,
        totalThisMonth: rawStatistics.total_this_month,
        averageCloseTime: rawStatistics.average_close_time,
      };
      yield put(updateStatistics(statistics));
    }
  } catch (error: any) {
    console.error('NCR Saga: Error occurred:', error);
    yield put(fetchNCRsFailure(error.message || 'Failed to fetch NCRs'));
  }
}

// Fetch NCR by ID Saga
function* fetchNCRByIdApi(action: PayloadAction<string>): Generator<any, void, any> {
  try {
    const ncr: NCR = yield call(ncrApi.getById, action.payload);
    yield put(fetchNCRByIdSuccess(ncr));
    
    // Fetch associated serials if they exist
    if (ncr.serial_data && ncr.serial_data.length > 0) {
      yield put(fetchSerialsStart(action.payload));
    }
  } catch (error: any) {
    yield put(fetchNCRByIdFailure(error.message || 'Failed to fetch NCR'));
  }
}

// Create NCR Saga
function* createNCRApi(action: PayloadAction<CreateNCRRequest>): Generator<any, void, any> {
  try {
    const newNCR: NCR = yield call(ncrApi.create, action.payload);
    yield put(createNCRSuccess(newNCR));
    
    // Refresh the NCRs list
    yield put(fetchNCRsStart());
  } catch (error: any) {
    yield put(createNCRFailure(error.message || 'Failed to create NCR'));
  }
}

// Update NCR Saga
function* updateNCRApi(action: PayloadAction<Partial<NCR>>): Generator<any, void, any> {
  try {
    const updatedNCR: NCR = yield call(ncrApi.update, action.payload);
    yield put(updateNCRSuccess(updatedNCR));
  } catch (error: any) {
    yield put(updateNCRFailure(error.message || 'Failed to update NCR'));
  }
}

// Delete NCR Saga
function* deleteNCRApi(action: PayloadAction<string>): Generator<any, void, any> {
  try {
    yield call(ncrApi.delete, action.payload);
    yield put(deleteNCRSuccess(action.payload));
    
    // Refresh the NCRs list
    yield put(fetchNCRsStart());
  } catch (error: any) {
    yield put(deleteNCRFailure(error.message || 'Failed to delete NCR'));
  }
}

// Fetch Dropdowns Saga
function* fetchDropdownsApi(): Generator<any, void, any> {
  try {
    // Fetch rejected serials data using stored procedure (like Angular version)
    const rejectedSerialsResponse = yield call(ncrApi.getOperationFailureWorkOrders);
    const rejectedSerials = rejectedSerialsResponse.data;
    
    // Extract unique work orders from rejected serials (matching Angular logic)
    const workOrdersMap = new Map();
    rejectedSerials.forEach((serial: any) => {
      if (!workOrdersMap.has(serial.WoId)) {
        workOrdersMap.set(serial.WoId, {
          Id: serial.WoId,
          WONum: serial.WoNum,
          // Include other work order properties as needed
          ...serial
        });
      }
    });
    const workOrders = Array.from(workOrdersMap.values());

    // Fetch other dropdown data in parallel
    const [
      ncTypes,
      partAttributes,
      partSecAttributes,
      partTercAttributes,
      serialStatuses,
      machines,
      departments,
      users
    ] = yield all([
      call(ncrApi.getNCTypes),
      call(ncrApi.getPartAttributes),
      call(ncrApi.getPartSecAttributes),
      call(ncrApi.getPartTercAttributes),
      call(ncrApi.getSerialStatuses),
      call(ncrApi.getMachines),
      call(ncrApi.getDepartments),
      call(ncrApi.getUsers)
    ]);
    
    yield put(fetchDropdownsSuccess({
      workOrders,
      ncTypes,
      partAttributes,
      partSecAttributes,
      partTercAttributes,
      serialStatuses,
      machines,
      departments,
      users,
      rejectedSerials // Store for use in operations/serials filtering
    }));
  } catch (error: any) {
    yield put(fetchDropdownsFailure(error.message || 'Failed to fetch dropdown data'));
  }
}

// Fetch Operations by Work Order Saga
function* fetchOperationsByWorkOrderApi(action: PayloadAction<string | null>): Generator<any, void, any> {
  try {
    if (!action.payload) {
      yield put(fetchDropdownsSuccess({ operations: [] }));
      return;
    }
    
    const operations = yield call(ncrApi.getOperations, action.payload);
    yield put(fetchDropdownsSuccess({ operations }));
  } catch (error: any) {
    console.error('Failed to fetch operations:', error);
    yield put(fetchDropdownsSuccess({ operations: [] }));
  }
}

// Fetch Serials by Work Order and Operation Saga
function* fetchSerialsByWorkOrderAndOperationApi(): Generator<any, void, any> {
  try {
    const state: RootState = yield select();
    const { selectedWorkOrderId, selectedOperationId } = state.ncr;
    
    if (!selectedWorkOrderId) {
      yield put(fetchDropdownsSuccess({ serials: [] }));
      return;
    }
    
    const serials = yield call(ncrApi.getSerialsByWorkOrder, selectedWorkOrderId, selectedOperationId || undefined);
    yield put(fetchDropdownsSuccess({ serials }));
  } catch (error: any) {
    console.error('Failed to fetch serials:', error);
    yield put(fetchDropdownsSuccess({ serials: [] }));
  }
}

// Fetch NC Sub Types by NC Type Saga
function* fetchNCSubTypesByNCTypeApi(action: PayloadAction<string | null>): Generator<any, void, any> {
  try {
    if (!action.payload) {
      yield put(fetchDropdownsSuccess({ ncSubTypes: [] }));
      return;
    }
    
    const ncSubTypes = yield call(ncrApi.getNCSubTypes, action.payload);
    yield put(fetchDropdownsSuccess({ ncSubTypes }));
  } catch (error: any) {
    console.error('Failed to fetch NC sub types:', error);
    yield put(fetchDropdownsSuccess({ ncSubTypes: [] }));
  }
}

// Serial Management Sagas
function* fetchSerialsApi(action: PayloadAction<string>): Generator<any, void, any> {
  try {
    const serials: NCRSerial[] = yield call(ncrApi.getSerials, action.payload);
    yield put(fetchSerialsSuccess(serials));
  } catch (error: any) {
    yield put(fetchSerialsFailure(error.message || 'Failed to fetch serials'));
  }
}

function* updateSerialApi(action: PayloadAction<UpdateSerialDispositionRequest>): Generator<any, void, any> {
  try {
    const updatedSerial: NCRSerial = yield call(ncrApi.updateSerial, action.payload);
    yield put(updateSerialSuccess(updatedSerial));
  } catch (error: any) {
    yield put(updateSerialFailure(error.message || 'Failed to update serial'));
  }
}

function* deleteSerialApi(action: PayloadAction<string>): Generator<any, void, any> {
  try {
    yield call(ncrApi.deleteSerial, action.payload);
    yield put(deleteSerialSuccess(action.payload));
  } catch (error: any) {
    yield put(deleteSerialFailure(error.message || 'Failed to delete serial'));
  }
}

// Defect Management Sagas
function* addDefectApi(action: PayloadAction<AddDefectRequest>): Generator<any, void, any> {
  try {
    const newDefect: PartDefect = yield call(ncrApi.addDefect, action.payload.ncr_detail_id, action.payload);
    yield put(addDefectSuccess(newDefect));
  } catch (error: any) {
    yield put(addDefectFailure(error.message || 'Failed to add defect'));
  }
}

function* deleteDefectApi(action: PayloadAction<string>): Generator<any, void, any> {
  try {
    yield call(ncrApi.deleteDefect, action.payload);
    yield put(deleteDefectSuccess(action.payload));
  } catch (error: any) {
    yield put(deleteDefectFailure(error.message || 'Failed to delete defect'));
  }
}

// Step-wise NCR Filling Sagas
function* saveGeneralDetailsApi(action: PayloadAction<{ ncrId: string; data: Partial<NCR> }>): Generator<any, void, any> {
  try {
    const updatedNCR: NCR = yield call(ncrApi.saveGeneralDetails, action.payload.ncrId, action.payload.data);
    yield put(saveGeneralDetailsSuccess(updatedNCR));
  } catch (error: any) {
    yield put(saveGeneralDetailsFailure(error.message || 'Failed to save general details'));
  }
}

function* saveSerialsDispositionApi(action: PayloadAction<{ ncrId: string; serialData: NCRSerial[] }>): Generator<any, void, any> {
  try {
    const updatedNCR: NCR = yield call(ncrApi.saveSerialsDisposition, action.payload.ncrId, action.payload.serialData);
    yield put(saveSerialsDispositionSuccess(updatedNCR));
  } catch (error: any) {
    yield put(saveSerialsDispositionFailure(error.message || 'Failed to save serials disposition'));
  }
}

function* saveClosingApi(action: PayloadAction<{ ncrId: string; data: any }>): Generator<any, void, any> {
  try {
    const updatedNCR: NCR = yield call(ncrApi.saveClosing, action.payload.ncrId, action.payload.data);
    yield put(saveClosingSuccess(updatedNCR));
    
    // Refresh the NCRs list to update the status
    yield put(fetchNCRsStart());
  } catch (error: any) {
    yield put(saveClosingFailure(error.message || 'Failed to save closing'));
  }
}

// Report Generation Saga
function* generateReportApi(action: PayloadAction<string>): Generator<any, void, any> {
  try {
    const reportData: NCRReportData = yield call(ncrApi.generateReport, action.payload);
    yield put(generateReportSuccess(reportData));
  } catch (error: any) {
    yield put(generateReportFailure(error.message || 'Failed to generate report'));
  }
}

// Watch for search term changes and debounce
function* watchSearchTerm(): Generator<any, void, any> {
  yield takeLatest('ncr/setSearchTerm', function* () {
    yield delay(500); // Debounce for 500ms
    yield put(fetchNCRsStart());
  });
}

// Watch for filter changes
function* watchFilters(): Generator<any, void, any> {
  yield takeLatest([
    'ncr/setStatusFilter',
    'ncr/setWorkOrderFilter',
    'ncr/setDateFromFilter',
    'ncr/setDateToFilter'
  ], function* () {
    yield put(fetchNCRsStart());
  });
}

// Watch for pagination changes
function* watchPagination(): Generator<any, void, any> {
  yield takeLatest(['ncr/setPage', 'ncr/setPageSize'], function* () {
    yield put(fetchNCRsStart());
  });
}

// Watch for Work Order selection changes
function* watchWorkOrderSelection(): Generator<any, void, any> {
  yield takeLatest('ncr/setSelectedWorkOrderId', function* (action: PayloadAction<string | null>) {
    yield call(fetchOperationsByWorkOrderApi, action);
    yield call(fetchSerialsByWorkOrderAndOperationApi);
  });
}

// Watch for Operation selection changes
function* watchOperationSelection(): Generator<any, void, any> {
  yield takeLatest('ncr/setSelectedOperationId', function* () {
    yield call(fetchSerialsByWorkOrderAndOperationApi);
  });
}

// Watch for NC Type selection changes
function* watchNCTypeSelection(): Generator<any, void, any> {
  yield takeLatest('ncr/setSelectedNCTypeId', function* (action: PayloadAction<string | null>) {
    yield call(fetchNCSubTypesByNCTypeApi, action);
  });
}

// Root NCR Saga
export default function* ncrSaga(): Generator<any, void, any> {
  yield all([
    // Main API calls
    takeEvery(fetchNCRsStart.type, fetchNCRsApi),
    takeEvery(fetchNCRByIdStart.type, fetchNCRByIdApi),
    takeEvery(createNCRStart.type, createNCRApi),
    takeEvery(updateNCRStart.type, updateNCRApi),
    takeEvery(deleteNCRStart.type, deleteNCRApi),
    
    // Dropdown data
    takeEvery(fetchDropdownsStart.type, fetchDropdownsApi),
    
    // Serial management
    takeEvery(fetchSerialsStart.type, fetchSerialsApi),
    takeEvery(updateSerialStart.type, updateSerialApi),
    takeEvery(deleteSerialStart.type, deleteSerialApi),
    
    // Defect management
    takeEvery(addDefectStart.type, addDefectApi),
    takeEvery(deleteDefectStart.type, deleteDefectApi),
    
    // Step-wise NCR filling
    takeEvery(saveGeneralDetailsStart.type, saveGeneralDetailsApi),
    takeEvery(saveSerialsDispositionStart.type, saveSerialsDispositionApi),
    takeEvery(saveClosingStart.type, saveClosingApi),
    
    // Report generation
    takeEvery(generateReportStart.type, generateReportApi),
    
    // Watch for UI changes
    watchSearchTerm(),
    watchFilters(),
    watchPagination(),
    watchWorkOrderSelection(),
    watchOperationSelection(),
    watchNCTypeSelection(),
  ]);
} 